import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

// ─── Global refresh throttle ──────────────────────────────────────────────────
// Ensures at most ONE network refresh happens per THROTTLE_MS window, no matter
// how many SIGNED_OUT events fire concurrently (e.g. during 429 storms).
const THROTTLE_MS = 30_000; // 30 seconds minimum between refresh calls

let refreshInFlight: Promise<Session | null> | null = null;
let lastRefreshAttempt = 0;

async function throttledRefresh(refreshToken: string): Promise<Session | null> {
  // If another refresh is already running, piggyback on it
  if (refreshInFlight) return refreshInFlight;

  const now = Date.now();
  if (now - lastRefreshAttempt < THROTTLE_MS) {
    // Too soon — skip the network call, return null (caller keeps existing state)
    return null;
  }

  lastRefreshAttempt = now;

  refreshInFlight = supabase.auth
    .refreshSession({ refresh_token: refreshToken })
    .then(({ data }) => data?.session ?? null)
    .catch(() => null)
    .finally(() => { refreshInFlight = null; });

  return refreshInFlight;
}

function isTokenExpired(session: Session): boolean {
  // expires_at is in seconds since epoch
  if (!session.expires_at) return false;
  return session.expires_at * 1000 < Date.now() + 60_000; // treat as expired if <1 min left
}
// ──────────────────────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const isExplicitSignOut = useRef(false);
  const sessionBackup = useRef<Session | null>(null);

  // Single debounce timer — collapses any number of rapid SIGNED_OUT events
  const recoveryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let isMounted = true;

    const applySession = (s: Session | null) => {
      if (!isMounted) return;
      if (s?.user) {
        sessionBackup.current = s;
      }
      setUser(s?.user ?? null);
      setSession(s);
    };

    // Called on every spurious SIGNED_OUT.
    // Strategy: if the backed-up token is still valid, reapply it locally without
    // a network call. Only hit the network when the access token is actually expired.
    const scheduleRecovery = (backup: Session) => {
      // Cancel any pending recovery — collapse the burst into one attempt
      if (recoveryTimer.current) clearTimeout(recoveryTimer.current);

      recoveryTimer.current = setTimeout(async () => {
        if (!isMounted) return;

        if (!isTokenExpired(backup)) {
          // Token is still valid — reapply it locally, no network call needed
          try {
            await supabase.auth.setSession({
              access_token: backup.access_token,
              refresh_token: backup.refresh_token,
            });
            applySession(backup);
          } catch {
            // setSession failed, try a throttled network refresh
            const refreshed = await throttledRefresh(backup.refresh_token);
            if (isMounted && refreshed) applySession(refreshed);
          }
        } else {
          // Token expired — do a throttled network refresh
          const refreshed = await throttledRefresh(backup.refresh_token);
          if (isMounted && refreshed) applySession(refreshed);
        }
      }, 500); // 500ms debounce — collapses bursts of SIGNED_OUT events
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!isMounted) return;

        if (event === "SIGNED_OUT") {
          if (isExplicitSignOut.current) {
            // Genuine sign-out — clear everything
            isExplicitSignOut.current = false;
            sessionBackup.current = null;
            if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
            setUser(null);
            setSession(null);
            return;
          }

          // Spurious 429-induced SIGNED_OUT — keep UI intact and schedule recovery
          const backup = sessionBackup.current;
          if (backup) scheduleRecovery(backup);
          return;
        }

        // INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED…
        if (newSession) {
          applySession(newSession);

          if (event === "SIGNED_IN") {
            const tokenSnapshot = newSession.access_token;
            // Defer the auth_events insert so it never blocks the callback
            setTimeout(async () => {
              if (!isMounted) return;
              try {
                const { data: { session: current } } = await supabase.auth.getSession();
                if (!current || current.access_token !== tokenSnapshot) return;
                await supabase.from("auth_events").insert({
                  user_id: newSession.user.id,
                  event_type: "sign_in",
                  metadata: {
                    provider: newSession.user.app_metadata?.provider || "email",
                    user_agent: navigator.userAgent,
                  },
                });
              } catch {
                // Non-critical — ignore silently
              }
            }, 500);
          }
        }
      }
    );

    // Initial session load — runs once, sets loading = false when done
    (async () => {
      try {
        const { data: { session: initial } } = await supabase.auth.getSession();
        if (isMounted) applySession(initial);
      } catch {
        if (isMounted) applySession(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    isExplicitSignOut.current = true;
    sessionBackup.current = null;
    if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
    setUser(null);
    setSession(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
}
